import {
  AccountData,
  Event,
  OrganizerType,
  TicketV2,
} from "@markit/common.types";
import { Colors } from "../../utils/colors";
import Skeleton from "react-loading-skeleton";
import { FormatCurrency } from "../../utils/FormatCurrency";
import { useTheme } from "../../hooks/useTheme";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import {
  getPromotersUserData,
  getTotalPromoterRevenue,
  getTotalRevShareTicketsSoldByUser,
} from "../../utils/eventUtils/eventUtils";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import ProfileItem from "../ProfileItem";
import { Icon } from "@iconify/react";
import PopupModalContainer from "../Containers/PopupModalContainer";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { getUserRoleTicket } from "../../utils/eventUtils/userTicketUtils";
import { CircularProgress } from "@mui/material";
import { EventOrganizerPromoterAnalytics } from "./EventOrganizerPromoterAnalytics";
import {
  handlePluralString,
  isRevShareOrganizerTicket,
} from "@markit/common.utils";

type EventPromoterAnalyticsProps = {
  event: Event;
  userTicket: TicketV2 | undefined;
};

export const EventPromoterAnalytics = (props: EventPromoterAnalyticsProps) => {
  const { event, userTicket } = props;
  const { theme } = useTheme();
  const { account } = useSelector(getAccountState);
  const { followingAccountData, accountData } = account;
  const [loading, setLoading] = useState(true);
  const [promotersAccountData, setPromotersAccountData] = useState<
    AccountData[]
  >([]);
  const [promotersTicketsSold, setPromotersTicketsSold] = useState<
    Map<string, number>
  >(new Map());
  const [promotersRevenue, setPromotersRevenue] = useState<Map<string, number>>(
    new Map()
  );
  const [promotersTotalRevenue, setPromotersTotalRevenue] = useState<
    Map<string, number>
  >(new Map());
  const [promoterItemVisible, setPromoterItemVisible] = useState<
    AccountData | undefined
  >(undefined);
  const [promoterItemTicketData, setPromoterItemTicketData] = useState<
    TicketV2 | undefined
  >(undefined);

  const styles = {
    analyticsContent: {
      gap: 24,
      color: Colors.BLACK,
    },

    analyticsText: {
      fontSize: 32,
    },

    containerView: {
      paddingInline: 14,
    },

    headerTitle: {
      fontSize: 16,
      fontWeight: 600,
    },

    overallContainerView: {
      width: "100%",
      backgroundColor: theme.SecondaryBG.backgroundColor,
      borderRadius: 20,
      marginBottom: 20,
    },

    sectionContainer: {
      paddingBlock: 14,
      backgroundColor: Colors.WHITE1,
    },

    sectionItem: {
      gap: 4,
      paddingInline: 14,
    },
  };

  const isHost = useMemo(
    () => userTicket && userTicket.role.type === OrganizerType.HOST,
    [userTicket]
  );

  useAsyncOnMount(async () => {
    // If the user is the host, get the promoters and cohosts, otherwise just the user's own data
    const promoterUsers = isHost
      ? await getPromotersUserData(event.id, followingAccountData)
      : [accountData];
    setPromotersAccountData(promoterUsers);

    const totalTicketsSold = new Map<string, number>();
    const totalRevenue = new Map<string, number>();
    const promoterRevenue = new Map<string, number>();
    for (let i = 0; i < promoterUsers.length; i++) {
      const [singlePromoterTicketsSold, revenues] = await Promise.all([
        getTotalRevShareTicketsSoldByUser(event.id, promoterUsers[i].uid),
        getTotalPromoterRevenue(event.id, promoterUsers[i].uid),
      ]);
      totalTicketsSold.set(promoterUsers[i].uid, singlePromoterTicketsSold);
      totalRevenue.set(promoterUsers[i].uid, revenues.totalRevenue);
      promoterRevenue.set(promoterUsers[i].uid, revenues.promoterRevenue);
    }
    setPromotersTicketsSold(totalTicketsSold);
    setPromotersTotalRevenue(totalRevenue);
    setPromotersRevenue(promoterRevenue);

    setLoading(false);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (promoterItemVisible) {
      // Fetch the promoter's ticket data if you press on their item
      const ticketData = await getUserRoleTicket(
        event.id,
        promoterItemVisible.uid
      );
      if (ticketData) {
        setPromoterItemTicketData(ticketData);
      }
    }
  }, [promoterItemVisible, event.id]);

  const totalPromoterTicketsSold = useMemo(
    () =>
      Array.from(promotersTicketsSold.values()).reduce(
        (acc, curr) => acc + curr,
        0
      ),
    [promotersTicketsSold]
  );
  const totalPromoterRevenue = useMemo(
    () =>
      Array.from(promotersRevenue.values()).reduce(
        (acc, curr) => acc + curr,
        0
      ),
    [promotersRevenue]
  );

  const promoterUserRevenue = useMemo(
    () =>
      promoterItemVisible
        ? promotersRevenue.get(promoterItemVisible.uid) || 0
        : 0,
    [promoterItemVisible, promotersRevenue]
  );

  const promoterUserTotalRevenue = useMemo(
    () =>
      promoterItemVisible
        ? promotersTotalRevenue.get(promoterItemVisible.uid) || 0
        : 0,
    [promoterItemVisible, promotersTotalRevenue]
  );

  const isPercentRevSplit = useMemo(
    () =>
      promoterItemTicketData &&
      promoterItemTicketData.role.revenuePercentSplit > 0,
    [promoterItemTicketData]
  );

  // Organizer promoter analytics (non-host)
  if (userTicket && isRevShareOrganizerTicket(userTicket)) {
    return (
      <EventOrganizerPromoterAnalytics
        userTicket={userTicket}
        promoterTicketsSold={totalPromoterTicketsSold}
        promoterRevenue={totalPromoterRevenue}
        event={event}
      />
    );
  }

  return (
    <>
      <StandardBorderedContainer containerStyles={styles.overallContainerView}>
        <div className="ColumnNormal" style={{ paddingBlock: 14 }}>
          <span style={{ ...styles.headerTitle, ...styles.containerView }}>
            Promoters
          </span>
          <HorizontalDivider altMargin={14} />
          <div style={{ ...styles.containerView }}>
            <div className="RowNormal" style={styles.analyticsContent}>
              <div>
                <div style={styles.analyticsText}>
                  {loading ? <Skeleton /> : totalPromoterTicketsSold}
                </div>
                <span className="smallBodySubtext">
                  Promoter{" "}
                  {handlePluralString("Ticket", totalPromoterTicketsSold)} Sold
                </span>
              </div>
              <div>
                <div style={styles.analyticsText}>
                  {loading ? (
                    <Skeleton />
                  ) : (
                    FormatCurrency(totalPromoterRevenue)
                  )}
                </div>
                <span className="smallBodySubtext">
                  Promoter Ticket Revenue
                </span>
              </div>
            </div>
          </div>
          {promotersAccountData.length > 0 ? (
            <>
              <HorizontalDivider altMargin={14} />
              <div
                className="ColumnNormal"
                style={{ gap: 10, ...styles.containerView }}
              >
                {promotersAccountData.map((user) => (
                  <ProfileItem
                    key={user.uid}
                    profileItem={user}
                    subtext={<span className="smallBodySubtext">Promoter</span>}
                    onItemPress={() => setPromoterItemVisible(user)}
                    hideViewIcon
                    rightIcon={
                      <div
                        className="AlignedRowSelect"
                        style={{ gap: 14 }}
                        onClick={() => setPromoterItemVisible(user)}
                      >
                        <div
                          className="ColumnNormal"
                          style={{ gap: 4, alignItems: "flex-end" }}
                        >
                          <span className="bodyMedium">
                            {promotersTicketsSold.get(user.uid) || 0}
                          </span>
                          <span className="smallBodySubtext">
                            {handlePluralString(
                              "Ticket",
                              promotersTicketsSold.get(user.uid) || 0
                            )}{" "}
                            Sold
                          </span>
                        </div>
                        <Icon
                          icon="ion:chevron-forward"
                          height={20}
                          style={{ color: Colors.GRAY1 }}
                        />
                      </div>
                    }
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>
      </StandardBorderedContainer>
      {promoterItemVisible ? (
        <PopupModalContainer
          noExit={true}
          closeOnOutsidePress
          noDivider
          valueComp={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <ProfileItem
                profileItem={promoterItemVisible}
                onItemPress={() => {}}
                subtext={<span className="smallBodySubtext">Promoter</span>}
                disabled
              />
              {promoterItemTicketData ? (
                <>
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    <span className="bodyMedium">Promoter Tickets</span>
                    <StandardBorderedContainer
                      containerStyles={styles.sectionContainer}
                    >
                      <div className="ColumnNormal" style={styles.sectionItem}>
                        <span className="bodySubtext">
                          Promoter Tickets Sold
                        </span>
                        <span className="bodyMedium">
                          {promotersTicketsSold.get(promoterItemVisible.uid) ||
                            0}
                        </span>
                      </div>
                    </StandardBorderedContainer>
                  </div>
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    <span className="bodyMedium">Promoter Ticket Revenue</span>
                    <StandardBorderedContainer
                      containerStyles={styles.sectionContainer}
                    >
                      <div className="ColumnNormal" style={styles.sectionItem}>
                        <span className="bodySubtext">
                          {isPercentRevSplit ? "Percent" : "Dollar"} Revenue
                          Share
                        </span>
                        <span className="bodyMedium">
                          {isPercentRevSplit
                            ? promoterItemTicketData.role.revenuePercentSplit +
                              "%"
                            : FormatCurrency(
                                promoterItemTicketData.role.revenueDollarSplit /
                                  100
                              )}
                        </span>
                      </div>
                      <HorizontalDivider altMargin={14} />
                      <div className="ColumnNormal" style={styles.sectionItem}>
                        <span className="bodySubtext">
                          Total Ticket Revenue
                        </span>
                        <span className="bodyMedium">
                          {FormatCurrency(
                            promotersTotalRevenue.get(
                              promoterItemVisible.uid
                            ) || 0
                          )}
                        </span>
                      </div>
                      <HorizontalDivider altMargin={14} />
                      <div
                        className="ColumnNormal"
                        style={{ gap: 4, paddingInline: 14 }}
                      >
                        <span className="bodySubtext">
                          Promoter Revenue{" "}
                          {isPercentRevSplit
                            ? `(${promoterItemTicketData.role.revenuePercentSplit}%)`
                            : null}
                        </span>
                        <span className="bodyMedium">
                          {FormatCurrency(promoterUserRevenue)}
                        </span>
                      </div>
                      <HorizontalDivider altMargin={14} />
                      <div className="ColumnNormal" style={styles.sectionItem}>
                        <span className="bodySubtext">
                          Host Revenue{" "}
                          {isPercentRevSplit
                            ? `(${
                                100 -
                                promoterItemTicketData.role.revenuePercentSplit
                              }%)`
                            : null}
                        </span>
                        <span className="bodyMedium">
                          {FormatCurrency(
                            Math.max(
                              0,
                              promoterUserTotalRevenue - promoterUserRevenue
                            )
                          )}
                        </span>
                      </div>
                    </StandardBorderedContainer>
                  </div>
                </>
              ) : (
                <div className="Centering">
                  <CircularProgress size={20} style={{ color: Colors.GRAY1 }} />
                </div>
              )}
            </div>
          }
          closeModal={() => {
            setPromoterItemVisible(undefined);
            setPromoterItemTicketData(undefined);
          }}
        />
      ) : null}
    </>
  );
};
